/**
 * Tailwind (Mantine-free) primitives registry.
 *
 * Lightweight HTML-element implementations of every primitive in the
 * PrimitivesRegistry interface.  No Mantine dependency at runtime.
 *
 * Styling is handled entirely by the existing CSS Modules + `--assistant-*`
 * custom properties.  Each component emits `data-primitives-*` attributes so
 * the agnostic CSS selectors in AIAssistant.module.css can target them, and
 * `data-triedge-ds="tailwind"` for Playwright fingerprinting.
 *
 * @module impl/tailwind
 */
import React, { forwardRef, useCallback, useRef, useEffect } from 'react';
import type {
  ActionIconProps,
  BadgeProps,
  ButtonProps,
  GroupProps,
  PaperProps,
  PrimitivesRegistry,
  ScrollAreaProps,
  SelectProps,
  SelectOption,
  SkeletonProps,
  StackProps,
  TextProps,
  TextInputProps,
  TextareaProps,
  TooltipProps,
  TransitionProps,
} from '../primitives.types.js';
import { autoResizeTextarea } from './textareaAutosize.js';

/* ---------- helpers ---------- */

/** Normalise Mantine-style spacing tokens (e.g. "xs", "sm", 4) to a CSS value. */
const spacingToCss = (value: string | number | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  const map: Record<string, string> = { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' };
  return map[value] ?? value;
};

const radiusToCss = (value: string | number | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  const map: Record<string, string> = { xs: '2px', sm: '4px', md: '8px', lg: '16px', xl: '32px' };
  return map[value] ?? value;
};

const sizeToCss = (value: string | number | undefined): string | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
};

/* ---------- ActionIcon ---------- */
const TailwindActionIcon = forwardRef<HTMLButtonElement, ActionIconProps>(
  function TailwindActionIcon({ variant, color, size, radius, className, style, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={className}
        style={style}
        data-primitives-action-icon=""
        data-variant={variant}
        data-triedge-ds="tailwind"
        {...rest}
      >
        {children}
      </button>
    );
  },
);

/* ---------- Badge ---------- */
const TailwindBadge = forwardRef<HTMLElement, BadgeProps>(
  function TailwindBadge({ variant, color, size, leftSection, className, style, children, ...rest }, ref) {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={className}
        style={style}
        data-primitives-badge=""
        data-variant={variant}
        data-color={color}
        data-triedge-ds="tailwind"
        {...rest}
      >
        {leftSection ? <span data-primitives-badge-left="">{leftSection}</span> : null}
        {children}
      </span>
    );
  },
);

/* ---------- Button ---------- */
const TailwindButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function TailwindButton({ variant, color, size, radius, loading, className, style, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={className}
        style={style}
        data-primitives-button=""
        data-variant={variant}
        data-triedge-ds="tailwind"
        disabled={loading || rest.disabled}
        aria-busy={loading || undefined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

/* ---------- Group ---------- */
const TailwindGroup = forwardRef<HTMLDivElement, GroupProps>(
  function TailwindGroup({ gap, justify, align, wrap, mt, className, style, children, ...rest }, ref) {
    const merged: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      gap: spacingToCss(gap),
      justifyContent: justify,
      alignItems: align ?? 'center',
      flexWrap: wrap,
      marginTop: spacingToCss(mt),
      ...style,
    };
    return (
      <div ref={ref} className={className} style={merged} data-primitives-group="" data-triedge-ds="tailwind" {...rest}>
        {children}
      </div>
    );
  },
);

/* ---------- Paper ---------- */
const TailwindPaper = forwardRef<HTMLDivElement, PaperProps>(
  function TailwindPaper({ shadow, radius, p, className, style, children, ...rest }, ref) {
    const merged: React.CSSProperties = {
      padding: spacingToCss(p),
      borderRadius: radiusToCss(radius),
      ...style,
    };
    return (
      <div ref={ref} className={className} style={merged} data-primitives-paper="" data-triedge-ds="tailwind" {...rest}>
        {children}
      </div>
    );
  },
);

/* ---------- ScrollArea ---------- */
const TailwindScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function TailwindScrollArea({ className, style, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={className}
        style={{ overflow: 'auto', flex: 1, ...style }}
        data-primitives-scroll-area=""
        data-primitives-scroll-viewport=""
        data-triedge-ds="tailwind"
        {...rest}
      >
        <div>{children}</div>
      </div>
    );
  },
);

/* ---------- Select ---------- */
const normaliseSelectData = (data?: Array<string | SelectOption>): SelectOption[] => {
  if (!data) return [];
  return data.map((d) => (typeof d === 'string' ? { value: d, label: d } : d));
};

const TailwindSelect = forwardRef<HTMLElement, SelectProps>(
  function TailwindSelect(
    {
      data,
      value,
      defaultValue,
      onChange,
      placeholder,
      size,
      radius,
      variant,
      allowDeselect,
      disabled,
      className,
      style,
      comboboxProps: _comboboxProps,
      ...rest
    },
    ref,
  ) {
    const options = normaliseSelectData(data);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        onChange?.(val === '' ? null : val);
      },
      [onChange],
    );

    return (
      <select
        ref={ref as React.Ref<HTMLSelectElement>}
        className={className}
        style={style}
        value={value ?? defaultValue ?? ''}
        onChange={handleChange}
        disabled={disabled}
        data-primitives-select=""
        data-primitives-select-input=""
        data-triedge-ds="tailwind"
        aria-label={rest['aria-label']}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  },
);

/* ---------- Skeleton ---------- */
const TailwindSkeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  function TailwindSkeleton({ height, width, radius, className, style, ...rest }, ref) {
    const merged: React.CSSProperties = {
      height: sizeToCss(height),
      width: sizeToCss(width),
      borderRadius: radiusToCss(radius) ?? '4px',
      background: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeleton-pulse 1.5s ease-in-out infinite',
      ...style,
    };
    return (
      <div ref={ref} className={className} style={merged} data-primitives-skeleton="" data-triedge-ds="tailwind" {...rest} />
    );
  },
);

/* ---------- Stack ---------- */
const TailwindStack = forwardRef<HTMLDivElement, StackProps>(
  function TailwindStack({ gap, p, className, style, children, ...rest }, ref) {
    const merged: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: spacingToCss(gap),
      padding: spacingToCss(p),
      ...style,
    };
    return (
      <div ref={ref} className={className} style={merged} data-primitives-stack="" data-triedge-ds="tailwind" {...rest}>
        {children}
      </div>
    );
  },
);

/* ---------- Text ---------- */
const sizeToFontSize = (size?: string): string | undefined => {
  if (!size) return undefined;
  const map: Record<string, string> = {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  };
  return map[size] ?? size;
};

const TailwindText = forwardRef<HTMLElement, TextProps>(
  function TailwindText({ size, fw, c, component, mt, mb, className, style, children, ...rest }, ref) {
    const Tag = (component ?? 'span') as React.ElementType;
    const merged: React.CSSProperties = {
      fontSize: sizeToFontSize(size),
      fontWeight: fw,
      color: c === 'dimmed' ? '#6b7280' : c,
      marginTop: spacingToCss(mt),
      marginBottom: spacingToCss(mb),
      ...style,
    };
    return (
      <Tag ref={ref} className={className} style={merged} data-primitives-text="" data-triedge-ds="tailwind" {...rest}>
        {children}
      </Tag>
    );
  },
);

/* ---------- TextInput ---------- */
const TailwindTextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TailwindTextInput({ size, radius, variant, label, error, className, style, ...rest }, ref) {
    return (
      <div data-primitives-text-input-wrapper="" data-triedge-ds="tailwind" className={className} style={style}>
        {label ? <label data-primitives-text-input-label="">{label}</label> : null}
        <input
          ref={ref}
          type="text"
          data-primitives-text-input=""
          data-primitives-input=""
          {...rest}
        />
        {error ? <span data-primitives-text-input-error="">{error}</span> : null}
      </div>
    );
  },
);

/* ---------- Textarea ---------- */
const TailwindTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function TailwindTextarea({ autosize, minRows, maxRows, size, className, style, onChange, ...rest }, ref) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const setRef = useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      },
      [ref],
    );

    const autoResize = useCallback(() => {
      autoResizeTextarea(innerRef.current, { autosize, minRows, maxRows });
    }, [autosize, maxRows, minRows]);

    useEffect(() => {
      autoResize();
    }, [autoResize, rest.value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        autoResize();
        onChange?.(e);
      },
      [autoResize, onChange],
    );

    return (
      <textarea
        ref={setRef}
        className={className}
        style={style}
        rows={minRows ?? 1}
        onChange={handleChange}
        data-primitives-textarea=""
        data-triedge-ds="tailwind"
        {...rest}
      />
    );
  },
);

/* ---------- Tooltip ---------- */
const TailwindTooltip: React.FC<TooltipProps> = function TailwindTooltip({
  label,
  children,
}) {
  // Lightweight: just use the native `title` attribute.
  // This avoids any positioning/portal logic while keeping the API surface intact.
  if (!label) return <>{children}</>;
  return React.cloneElement(children, {
    title: typeof label === 'string' ? label : undefined,
  } as Record<string, unknown>);
};

/* ---------- Transition ---------- */
const TailwindTransition: React.FC<TransitionProps> = function TailwindTransition({
  mounted,
  duration: _duration = 200,
  children,
}) {
  if (!mounted) return null;
  // When mounted, render immediately with empty styles (no animation library needed).
  return <>{children({})}</>;
};

/* ---------- Registry export ---------- */
export const tailwindRegistry: PrimitivesRegistry = {
  ActionIcon: TailwindActionIcon,
  Badge: TailwindBadge as PrimitivesRegistry['Badge'],
  Button: TailwindButton,
  Group: TailwindGroup,
  Paper: TailwindPaper,
  ScrollArea: TailwindScrollArea,
  Select: TailwindSelect as PrimitivesRegistry['Select'],
  Skeleton: TailwindSkeleton,
  Stack: TailwindStack,
  Text: TailwindText as PrimitivesRegistry['Text'],
  TextInput: TailwindTextInput,
  Textarea: TailwindTextarea,
  Tooltip: TailwindTooltip,
  Transition: TailwindTransition,
};
